import { DOCUMENT } from '@angular/common';
import { Component, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import {
    MapCoordinates,
    MapIcons,
    PlannerFacade,
    RealtimeFacade,
    RoutesFacade,
    RouteStopsFacade,
    StopsFacade
} from '@ridango/components';
import { BaseComponent, WebSocketService } from '@ridango/core';

import { fromEvent } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { FeatureFlagService } from '@ridango/feature-flags';
import Env from '@ridango/core/env';
import { FEATURE_FLAGS } from '@ridango/core';

@Component({
    templateUrl: './schedule-parent-page.component.html',
    styleUrls: ['./schedule-parent-page.component.scss']
})
export class ScheduleParentPageComponent extends BaseComponent implements OnInit, OnDestroy {
    private location;

    zoom = 13;
    mapCenter: MapCoordinates = this.env.MAP.COORDINATES;
    isFullMap = false;
    polylineColor = 'var(--color-brand-primary)';
    resRobotTrips: number[][] = null;
    stopIds: number[] = [];
    selectedDestination;
    selectedOrigin;
    stops = [];
    clickedStop = null;
    stopsLoaded = false;

    iconStop = MapIcons('stop', this.env.THEME_NAME);
    iconStopRetina = MapIcons('stopRetina', this.env.THEME_NAME);
    iconSelectedStop = MapIcons('stopHighlighted', this.env.THEME_NAME);
    iconSelectedStopRetina = MapIcons('stopHighlightedRetina', this.env.THEME_NAME);
    iconActiveStop = MapIcons('stopSelected', this.env.THEME_NAME);
    iconActiveStopRetina = MapIcons('stopSelectedRetina', this.env.THEME_NAME);
    iconOrigin = MapIcons('pin');
    iconDestination = MapIcons('origin');

    allStops$ = this.stopsFacade.filtered$;
    stops$ = this.stopsFacade.byId$;
    selectedId$ = this.routeStopsFacade.selectedId$;
    resRobotTrip$ = this.plannerFacade.selectedTrip$.pipe(filter((trip) => !!trip));
    routeStopIds$ = this.routeStopsFacade.ids$;
    routePolyline$ = this.routeStopsFacade.routePolyline$;
    selectedOrigin$ = this.plannerFacade.selectedOrigin$;
    selectedDestination$ = this.plannerFacade.selectedDestination$;

    // Display buses on map
    busLocations$ = this.realtime.locations$;
    selectedBus$ = this.realtime.selectedLocation$;
    busRoute$ = this.realtime.shapes$;
    showBookARide = true;

    constructor(
        @Inject(DOCUMENT) private document: any,
        private stopsFacade: StopsFacade,
        private routesFacade: RoutesFacade,
        private routeStopsFacade: RouteStopsFacade,
        private plannerFacade: PlannerFacade,
        private router: Router,
        private translate: TranslateService,
        private realtime: RealtimeFacade,
        private webSocket: WebSocketService,
        private zone: NgZone,
        @Inject('ENV') public env: Env,
        private featureFlags: FeatureFlagService
    ) {
        super();
    }

    ngOnInit() {
        this.featureFlags
            .getFlags(FEATURE_FLAGS.BookARide)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((flagValue) => {
                this.showBookARide = flagValue;
            });

        this.allStops$.pipe(takeUntil(this.componentDestroyed$)).subscribe((stops) => {
            if (stops.length === 0 && !this.stopsLoaded) {
                this.stopsFacade.getStops();
                this.stopsLoaded = true;
            } else {
                this.stops = stops.map((s) => ({ ...s, latlng: { lat: s.stop_lat, lng: s.stop_lon } }));
            }
        });

        this.routesFacade.ids$.pipe(takeUntil(this.componentDestroyed$)).subscribe((ids) => {
            if (ids.length === 0) {
                this.routesFacade.fetchAllStopsWithRoutes();
            }
        });

        this.routesFacade.ids$.pipe(takeUntil(this.componentDestroyed$)).subscribe((ids) => {
            if (ids.length === 0) {
                this.routesFacade.fetchAllRoutes();
            }
        });

        this.selectedDestination$.pipe(takeUntil(this.componentDestroyed$)).subscribe((destination) => {
            this.selectedDestination = destination;
            this.resRobotTrips = null;
        });

        this.selectedOrigin$.pipe(takeUntil(this.componentDestroyed$)).subscribe((origin) => {
            this.selectedOrigin = origin;
            this.resRobotTrips = null;
        });

        this.resRobotTrip$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((trip) => (this.resRobotTrips = this.plannerFacade.getTripLatLngs(trip)));

        this.routeStopIds$.pipe(takeUntil(this.componentDestroyed$)).subscribe((stopIds) => {
            if (stopIds.length > 0) {
                this.stopsFacade.setSelectedStops(stopIds);
            }

            this.stopIds = stopIds;
        });

        this.zone.runOutsideAngular(() => {
            this.webSocket
                .connect('/ws/realtime')
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((buses) => {
                    this.realtime.setLocations(buses);
                });
        });

        fromEvent(window.document, 'keydown')
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((event: KeyboardEvent) => {
                if (event.key === 'Escape' || event.key === 'Esc') {
                    this.clearSelectedBus();
                }
            });
    }

    onMapReady() {
        const payload = {
            regionId: this.env.REGION_ID,
            topLeftCoordinates: {
                longitude: 0,
                latitude: 180
            },
            bottomRightCoordinates: {
                longitude: 180,
                latitude: -180
            }
        };

        this.webSocket.send(payload);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.clearSelectedBus();
        this.plannerFacade.resetPlanner();
        this.zone.runOutsideAngular(() => this.webSocket.closeConnection());
    }

    toggleMapSize() {
        this.isFullMap = !this.isFullMap;
    }

    onBusClicked({ bus, event }) {
        event.stopPropagation();
        this.realtime.setSelectedLocation(bus);
        this.realtime.fetchShapes(bus.trip_id);
    }

    onPointClicked(coordinates) {
        this.location = { lat: String(coordinates.lat), lon: String(coordinates.lng) };
    }

    onDepartureClicked(stop = null) {
        stop ? this.plannerFacade.setOrigin(stop) : this.plannerFacade.setOrigin(this.location);
        this.router.navigate([this.translate.currentLang, 'schedule', 'planner']);
    }

    onDestinationClicked(stop = null) {
        stop ? this.plannerFacade.setDestination(stop) : this.plannerFacade.setDestination(this.location);
        this.router.navigate([this.translate.currentLang, 'schedule', 'planner']);
    }

    onStopClicked(stop) {
        this.clickedStop = stop;
    }

    clearSelectedBus() {
        this.realtime.setShapes([]);
        this.realtime.setSelectedLocation(null);
    }
}
